/**
 * A simulated A&D balance for development, demos and tests: pours move the mass smoothly, readings are
 * noisy and flagged US while settling, then ST; it answers the A&D commands the app sends (?TN, ?SN, SIR,
 * C, Q, S, T, R). The model is pure (`advance(ms)` returns the lines sent meanwhile); the transport wraps it
 * with a timer.
 */
import type { BalanceTransport, TransportCallbacks } from "./types";

export interface SimulatorOptions {
  /** Readability in grams (0.001 for the FX-123). */
  resolution: number;
  /** Time a reading takes to settle after the last change. */
  settleMs: number;
  /** Peak noise in grams while settling. */
  noise: number;
  /** Stream period (the A&D SIR stream sends about 6–10 lines a second). */
  intervalMs: number;
  /** Default pour duration. */
  pourMs: number;
  capacity: number;
  model: string;
  serial: string;
  /** Random source in [0, 1); pass a seeded one in tests. */
  random: () => number;
}

export const DEFAULT_SIMULATOR_OPTIONS: SimulatorOptions = {
  resolution: 0.001,
  settleMs: 700,
  noise: 0.004,
  intervalMs: 150,
  pourMs: 600,
  capacity: 122,
  model: "Simulated FX-120i",
  serial: "SIM00001",
  random: Math.random,
};

interface Pour {
  from: number;
  to: number;
  start: number;
  end: number;
}

interface Scheduled {
  at: number;
  action: () => void;
}

export class SimulatedBalance {
  readonly options: SimulatorOptions;
  private now = 0;
  private mass = 0;
  private tareOffset = 0;
  private activePour: Pour | null = null;
  private settledAt = -Infinity;
  private streaming = false;
  private waitingForStable = false;
  private sinceEmit = 0;
  private scheduled: Scheduled[] = [];

  constructor(options: Partial<SimulatorOptions> = {}) {
    this.options = { ...DEFAULT_SIMULATOR_OPTIONS, ...options };
  }

  get time(): number {
    return this.now;
  }

  /** Grams on the pan minus the tare (what the display shows once settled). */
  get net(): number {
    return this.mass - this.tareOffset;
  }

  get isStable(): boolean {
    return this.activePour === null && this.now - this.settledAt >= this.options.settleMs;
  }

  get isStreaming(): boolean {
    return this.streaming;
  }

  /** Advances the clock by `ms`; returns the lines the balance sends in that time. */
  advance(ms: number): string[] {
    const lines: string[] = [];
    let remaining = Math.max(0, ms);
    while (remaining > 0) {
      const step = Math.min(remaining, this.options.intervalMs - this.sinceEmit);
      this.tick(step);
      this.sinceEmit += step;
      remaining -= step;
      if (this.sinceEmit >= this.options.intervalMs) {
        this.sinceEmit = 0;
        if (this.streaming) lines.push(this.line());
        if (this.waitingForStable && this.isStable) {
          this.waitingForStable = false;
          lines.push(this.line());
        }
      }
    }
    return lines;
  }

  /** Pours `grams` (negative removes) over `durationMs`, continuing from wherever a running pour is. */
  pourGrams(grams: number, durationMs = this.options.pourMs): void {
    const from = this.mass;
    this.activePour = { from, to: from + grams, start: this.now, end: this.now + Math.max(1, durationMs) };
  }

  /** Pours until the display shows `target` g, or `overshoot` (0.04 = 4 %) more than the amount still missing. */
  pourTo(target: number, { overshoot = 0, durationMs }: { overshoot?: number; durationMs?: number } = {}): void {
    this.pourGrams((target - this.net) * (1 + overshoot), durationMs);
  }

  /** Puts a vessel on the pan (a quick step change that then settles). */
  placeVessel(grams: number): void {
    this.pourGrams(grams, 200);
  }

  setMass(grams: number): void {
    this.activePour = null;
    this.mass = grams;
    this.settledAt = this.now;
  }

  /** Runs `action` after `delayMs` of simulated time (scripted pours for demos and tests). */
  schedule(delayMs: number, action: () => void): void {
    this.scheduled.push({ at: this.now + delayMs, action });
    this.scheduled.sort((a, b) => a.at - b.at);
  }

  /** Handles one command (without the line ending); returns the immediate replies. */
  write(command: string): string[] {
    switch (command.trim().toUpperCase()) {
      case "?TN":
        return [`TN,"${this.options.model}"`];
      case "?SN":
        return [`SN,"${this.options.serial}"`];
      case "?ID":
        return ['ID,"SIM"'];
      case "SIR":
        this.streaming = true;
        return [];
      case "C":
        this.streaming = false;
        this.waitingForStable = false;
        return [];
      case "Q":
      case "SI":
        return [this.line()];
      case "S":
        if (this.isStable) return [this.line()];
        this.waitingForStable = true;
        return [];
      case "T":
      case "R":
      case "Z":
        this.tareOffset = this.activePour ? this.activePour.to : this.mass;
        return ["\u0006"];
      default:
        return ["EC,E01"];
    }
  }

  /** The current line in A&D standard format, e.g. `ST,+00012.345  g`. */
  line(): string {
    if (this.mass > this.options.capacity) return "OL,+9999999E+19";
    const { resolution } = this.options;
    const decimals = Math.max(0, Math.round(-Math.log10(resolution)));
    let value = this.net;
    if (!this.isStable) {
      const decay = this.activePour ? 1 : Math.max(0, 1 - (this.now - this.settledAt) / this.options.settleMs);
      value += this.options.noise * decay * (this.options.random() * 2 - 1);
    }
    const rounded = Math.round(value / resolution) * resolution;
    const sign = rounded < 0 && Math.abs(rounded) >= resolution / 2 ? "-" : "+";
    const digits = Math.abs(rounded)
      .toFixed(decimals)
      .padStart(decimals ? 6 + decimals : 7, "0");
    return `${this.isStable ? "ST" : "US"},${sign}${digits}  g`;
  }

  private tick(ms: number): void {
    const target = this.now + ms;
    while (this.scheduled.length && (this.scheduled[0]?.at ?? Infinity) <= target) {
      const next = this.scheduled.shift();
      if (!next) break;
      this.advancePour(next.at);
      next.action();
    }
    this.advancePour(target);
  }

  private advancePour(until: number): void {
    this.now = Math.max(this.now, until);
    const pour = this.activePour;
    if (!pour) return;
    if (this.now >= pour.end) {
      this.mass = pour.to;
      this.activePour = null;
      this.settledAt = pour.end;
    } else {
      this.mass = pour.from + ((pour.to - pour.from) * (this.now - pour.start)) / (pour.end - pour.start);
    }
  }
}

export interface SimulatorTransport extends BalanceTransport {
  readonly simulator: SimulatedBalance;
}

/** The simulator behind a real-time transport (lines arrive on a timer, like a streaming balance). */
export function createSimulatorTransport(simulator = new SimulatedBalance()): SimulatorTransport {
  let timer: ReturnType<typeof setInterval> | null = null;
  let callbacks: TransportCallbacks | null = null;
  let last = 0;
  return {
    kind: "simulator",
    label: "Simulator",
    canWrite: true,
    simulator,
    open(nextCallbacks) {
      callbacks = nextCallbacks;
      last = performance.now();
      timer = setInterval(
        () => {
          const now = performance.now();
          const lines = simulator.advance(now - last);
          last = now;
          for (const line of lines) callbacks?.onLine(line);
        },
        Math.max(10, simulator.options.intervalMs / 3),
      );
      return Promise.resolve();
    },
    write(text) {
      const replies = simulator.write(text.trim());
      queueMicrotask(() => {
        for (const line of replies) callbacks?.onLine(line);
      });
      return Promise.resolve();
    },
    close() {
      if (timer !== null) clearInterval(timer);
      timer = null;
      const closing = callbacks;
      callbacks = null;
      closing?.onClose();
      return Promise.resolve();
    },
  };
}
